import { create } from "zustand";

export type DomainEventType = "entity.created" | "lineage.updated" | string;

export interface DomainEventScope {
  projectId?: string | null;
  conversationId?: string | null;
}

export type DomainCreatedEntity =
  | {
      type: "document";
      id: string;
      title: string;
      sizeBytes?: number | null;
    }
  | {
      type: "assumption";
      id: string;
      statement: string;
      evidence: number;
      importance: number;
    }
  | {
      type: "hypothesis";
      id: string;
      statement: string;
      theme?: string | null;
    }
  | {
      type: "experiment";
      id: string;
      title: string;
      experimentType?: string | null;
    };

import type { DocumentIndexingStatus } from "@/services/documentService";
import type { CNSSourceImportBatchStatus } from "@/models/sourceConnection";

export interface DomainEventPayload {
  createdEntities?: DomainCreatedEntity[];
  toolName?: string;
  toolCallId?: string;
  documentId?: string;
  documentName?: string;
  documentSize?: number;
  documentType?: string;
  indexingStatus?: DocumentIndexingStatus;
  indexingError?: string | null;
  action?: "attached" | "detached";
  edgeId?: string;
  subjectType?: string;
  subjectPublicId?: string;
  targetType?: string;
  targetPublicId?: string;
  batchId?: string;
  connectionId?: string;
  batchStatus?: CNSSourceImportBatchStatus;
  totalCount?: number;
  completedCount?: number;
  messageId?: string;
  changeKind?: "PROGRESS" | "COMPLETED" | "EDITED";
  assumptionId?: string;
  hypothesisId?: string;
  experimentId?: string;
  conversationId?: string | null;
}

export interface DomainEvent {
  eventType: DomainEventType;
  scope: DomainEventScope;
  entityTypesAffected?: string[];
  source?: string;
  payload?: DomainEventPayload;
  timestamp: number;
  revision: number;
}

export interface PublishDomainEventInput {
  eventType: DomainEventType;
  scope: DomainEventScope;
  entityTypesAffected?: string[];
  source?: string;
  payload?: DomainEventPayload;
}

interface DomainEventStore {
  revisionByScopeKey: Record<string, number>;
  revisionByScopeKeyAndType: Record<string, number>;
  latestEventByScopeKey: Record<string, DomainEvent | undefined>;
  latestEventByScopeKeyAndType: Record<string, DomainEvent | undefined>;
  publishDomainEvent: (event: PublishDomainEventInput) => DomainEvent;
  getRevision: (scope: DomainEventScope) => number;
  getEventTypeRevision: (scope: DomainEventScope, eventType: DomainEventType) => number;
  getLatestEvent: (scope: DomainEventScope) => DomainEvent | undefined;
  getLatestEventByType: (
    scope: DomainEventScope,
    eventType: DomainEventType,
  ) => DomainEvent | undefined;
}

const GLOBAL_SCOPE = "*";

export function getDomainEventScopeKey(scope: DomainEventScope): string {
  const projectId = scope.projectId ?? GLOBAL_SCOPE;
  const conversationId = scope.conversationId ?? GLOBAL_SCOPE;
  return `${projectId}:${conversationId}`;
}

export function getDomainEventScopeAndTypeKey(
  scope: DomainEventScope,
  eventType: DomainEventType,
): string {
  return `${getDomainEventScopeKey(scope)}:${eventType}`;
}

export const useDomainEventStore = create<DomainEventStore>((set, get) => ({
  revisionByScopeKey: {},
  revisionByScopeKeyAndType: {},
  latestEventByScopeKey: {},
  latestEventByScopeKeyAndType: {},

  publishDomainEvent: (event: PublishDomainEventInput) => {
    const scopeKey = getDomainEventScopeKey(event.scope);
    const scopeAndTypeKey = getDomainEventScopeAndTypeKey(event.scope, event.eventType);
    const nextRevision = (get().revisionByScopeKey[scopeKey] ?? 0) + 1;
    const nextTypedRevision = (get().revisionByScopeKeyAndType[scopeAndTypeKey] ?? 0) + 1;
    const domainEvent: DomainEvent = {
      ...event,
      timestamp: Date.now(),
      revision: nextRevision,
    };

    set((state) => ({
      revisionByScopeKey: {
        ...state.revisionByScopeKey,
        [scopeKey]: nextRevision,
      },
      revisionByScopeKeyAndType: {
        ...state.revisionByScopeKeyAndType,
        [scopeAndTypeKey]: nextTypedRevision,
      },
      latestEventByScopeKey: {
        ...state.latestEventByScopeKey,
        [scopeKey]: domainEvent,
      },
      latestEventByScopeKeyAndType: {
        ...state.latestEventByScopeKeyAndType,
        [scopeAndTypeKey]: domainEvent,
      },
    }));

    return domainEvent;
  },

  getRevision: (scope: DomainEventScope) => {
    const scopeKey = getDomainEventScopeKey(scope);
    return get().revisionByScopeKey[scopeKey] ?? 0;
  },

  getEventTypeRevision: (scope: DomainEventScope, eventType: DomainEventType) => {
    const scopeAndTypeKey = getDomainEventScopeAndTypeKey(scope, eventType);
    return get().revisionByScopeKeyAndType[scopeAndTypeKey] ?? 0;
  },

  getLatestEvent: (scope: DomainEventScope) => {
    const scopeKey = getDomainEventScopeKey(scope);
    return get().latestEventByScopeKey[scopeKey];
  },

  getLatestEventByType: (scope: DomainEventScope, eventType: DomainEventType) => {
    const scopeAndTypeKey = getDomainEventScopeAndTypeKey(scope, eventType);
    return get().latestEventByScopeKeyAndType[scopeAndTypeKey];
  },
}));
